import * as React from 'react'
import { withStyles, WithStyles } from '@zn-material-ui'
import Store from 'store'

import { Login } from '@services/api'
import { MD5 } from '@utils/lib'
import { IDENTITY_CODE_TIME, PAGE_TYPE } from '@const/login'

import CaptchaDialog from '../components/CaptchaDialog'
import CheckIdentityForm from '../components/CheckIdentityCodeForm'
import Agreement from '../components/Agreement'
import accountFormStyles from '../components/styles/AccountFormStyle'
import { IProps as ICommonProps } from './index'
import { transform } from '@utils/intl_lang'
const validatePhone = (value: string) => /^[0-9]{6,20}$/.test(value) // 6-20位数

interface IProps extends ICommonProps, WithStyles<typeof accountFormStyles> {
  headText: string
  description: string
  requestType: string
  nextLabel: string
  handleComplete: () => void
  confirmPwdLoading: boolean
  needAgreement?: boolean
}

const initialState = {
  cellphoneNumber: '',
  identityCode: '',
  captchaDialogOpen: false,
  count: IDENTITY_CODE_TIME,
  canGetIdentityCode: true,
  pageType: PAGE_TYPE.CHECK_IDENTITY_CODE,
  cellphoneError: false,
  identityCodeError: false,
  checkIdentityLoading: false,
  area: (Store.get('areaCode') && `00${Store.get('areaCode')}`) || '',
}

type IState = Readonly<typeof initialState>

class AccountForm extends React.Component<IProps, IState> {
  public state = initialState
  private timer: NodeJS.Timeout
  public componentWillUnmount() {
    clearInterval(this.timer)
  }

  public render() {
    const { classes, headText, description, requestType } = this.props
    const { cellphoneNumber, captchaDialogOpen, pageType, area } = this.state
    if (pageType === PAGE_TYPE.AGREEMENT) {
      return (
        <>
          <div className={classes.formContainer}>
            <div className={classes.agreementContainer}>
              <div className={classes.back} onClick={this.handleBack} />
              <Agreement handleAgree={this.handleAgree} />
            </div>
          </div>
        </>
      )
    }
    return (
      <>
        <div className={classes.formContainer}>
          <div className={classes.loginContainer}>
            <div className={classes.back} onClick={this.handleBack} />
            <div className={classes.title}>
              <div className={classes.headText}>{headText}</div>
              <div className={classes.description}>{description}</div>
              <div className={classes.titleLine} />
            </div>
            {this.getPage()}
          </div>
        </div>
        {captchaDialogOpen && (
          <CaptchaDialog
            area={area}
            onSuccess={this.onSuccess}
            onClose={this.onClose}
            cellphoneNumber={cellphoneNumber}
            requestType={requestType}
          />
        )}
      </>
    )
  }
  private onAreaChange = (area: string) => {
    this.setState({ area: (area !== '86' && `00${area}`) || '' })
    Store.set('areaCode', (area !== '86' && area) || '')
  }
  private getPage = () => {
    const {
      cellphoneNumber,
      identityCode,
      count,
      canGetIdentityCode,
      cellphoneError,
      identityCodeError,
      checkIdentityLoading,
    } = this.state

    const { nextLabel, needAgreement } = this.props

    return (
      <CheckIdentityForm
        cellphoneNumber={cellphoneNumber}
        mobilePhoneChange={this.mobilePhoneChange}
        identityCodeChange={this.identityCodeChange}
        identityCode={identityCode}
        canGetIdentityCode={canGetIdentityCode}
        handleGetIdentityCode={this.handleGetIdentityCode}
        cellphoneBlur={this.cellphoneBlur}
        handleNext={this.handleNext}
        needAgreement={needAgreement}
        handleAgreement={this.handleAgreement}
        count={count}
        buttonLabel={nextLabel}
        cellphoneError={cellphoneError}
        identityCodeError={identityCodeError}
        loading={checkIdentityLoading}
        onAreaChange={this.onAreaChange}
        // "确定即代表同意"
        agreeLabel={transform('Login_confirm_agree')}
      />
    )
  }

  private cellphoneBlur = (event: any) => {
    const cellphoneError = !validatePhone(event.target.value)
    this.setState({ cellphoneError })
  }

  private handleAgree = () => {
    this.setState({ pageType: PAGE_TYPE.CHECK_IDENTITY_CODE })
  }

  private handleAgreement = () => {
    this.setState({ pageType: PAGE_TYPE.AGREEMENT })
  }

  private handleNext = async (areaCode: string) => {
    const { cellphoneNumber, identityCode, cellphoneError } = this.state
    const area = Store.get('areaCode') ? `00${Store.get('areaCode')}` : areaCode
    Store.set('areaCode', area.slice(2))

    if (cellphoneNumber === '' || identityCode === '') {
      // '手机号和验证码不能为空'
      global.$message(transform('Login_num_not_empty'))
      return
    }
    if (cellphoneError) {
      // '手机号长度不合法'
      global.$message(transform('Login_illegal_mobile_number'))
      return
    }

    this.setState({ checkIdentityLoading: true, area })
    const result = await Login.bindPhoneAndEnterpriseIdentity({
      // 绑定手机号
      phoneNum: `${area}${cellphoneNumber}`,
      otp: identityCode,
      deviceId: MD5(cellphoneNumber),
    })
    this.setState({ checkIdentityLoading: false })

    if (+result.code === 0) {
      this.props.handleComplete()
    } else {
      global.$message(result.message)
    }
  }

  private openCaptchaDialog = () => {
    this.setState({ captchaDialogOpen: true })
  }

  private onClose = () => {
    this.setState({ captchaDialogOpen: false })
  }

  private onSuccess = () => {
    this.onClose()
    const { canGetIdentityCode } = this.state
    if (!canGetIdentityCode) {
      return
    }
    this.setState({ canGetIdentityCode: false })
    let { count } = this.state
    this.timer = setInterval(() => {
      this.setState({ count: --count }, () => {
        if (count === 0) {
          clearInterval(this.timer)
          this.setState({
            canGetIdentityCode: true,
            count: IDENTITY_CODE_TIME,
          })
        }
      })
    }, 1000)
  }

  private handleBack = () => {
    this.props.history.push('/one-login')
  }

  private mobilePhoneChange = (event: any) => {
    this.setState({ cellphoneNumber: event.target.value })
  }

  private identityCodeChange = (event: any) => {
    this.setState({ identityCode: event.target.value })
  }

  private handleGetIdentityCode = () => {
    const { canGetIdentityCode, cellphoneNumber, cellphoneError } = this.state
    if (!canGetIdentityCode || cellphoneError) {
      return
    }
    if (cellphoneNumber.trim() === '') {
      // '请填写手机号！'
      global.$message(transform('Login_enter_mobile_number'))
      return
    }
    this.openCaptchaDialog()
  }
}

export default withStyles(accountFormStyles)(AccountForm)
